import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Data that is on screen before the network is touched.
 *
 * A dashboard is opened, navigated away from, and opened again all day. Fetching
 * on every mount means a spinner every time and a burst of aggregate queries
 * every time, for numbers that did not move.
 *
 * So the last response is kept, and reads go:
 *
 *   **fresh** — inside the TTL, rendered from cache and no request is made at
 *   all. This is the common case and the one that removes the load.
 *
 *   **stale** — rendered from cache immediately, then refreshed in the
 *   background. The page is never blank and never blocks; the numbers update in
 *   place a moment later.
 *
 *   **cold** — nothing cached, so it loads the way it always did.
 *
 * The cache lives in `localStorage`, so a full reload is instant too, and in
 * memory so two components asking for the same key share one request.
 */

/** In-flight requests, keyed, so a double mount does not double-fetch. */
const inFlight = new Map();

/** Mirrors localStorage, so a re-render need not re-parse JSON. */
const memory = new Map();

const storageKey = (key) => `cache:${key}`;

function readCache(key) {
    if (memory.has(key)) return memory.get(key);

    try {
        const raw = localStorage.getItem(storageKey(key));
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.at !== "number") return null;

        memory.set(key, parsed);
        return parsed;
    } catch {
        // Private mode, cleared storage, or a half-written entry. A cache that
        // cannot be read is a cache miss, never an error.
        return null;
    }
}

function writeCache(key, data) {
    const entry = { at: Date.now(), data };
    memory.set(key, entry);
    try {
        localStorage.setItem(storageKey(key), JSON.stringify(entry));
    } catch {
        // Quota, or storage disabled. The in-memory copy still serves this
        // session, which is the half that matters most.
    }
}

/** Drop one key, or everything, after a write that makes the numbers wrong. */
export function invalidateCache(key) {
    if (key === undefined) {
        memory.clear();
        try {
            for (const k of Object.keys(localStorage)) {
                if (k.startsWith("cache:")) localStorage.removeItem(k);
            }
        } catch {
            /* nothing to do */
        }
        return;
    }

    memory.delete(key);
    try {
        localStorage.removeItem(storageKey(key));
    } catch {
        /* nothing to do */
    }
}

/**
 * The same cache, without the hook.
 *
 * For call sites that already have their own loading state and a transform to
 * run over the response — restructuring those into the hook would mean pulling
 * the fetch apart from the code that shapes its result, for no gain.
 *
 * @param {string} key
 * @param {() => Promise<any>} fetcher
 * @param {number} ttlMs
 */
export async function cachedCall(key, fetcher, ttlMs = 60_000) {
    const entry = readCache(key);
    if (entry && Date.now() - entry.at < ttlMs) return entry.data;

    let promise = inFlight.get(key);
    if (!promise) {
        promise = Promise.resolve().then(fetcher);
        inFlight.set(key, promise);
        promise.finally(() => inFlight.delete(key));
    }

    const result = await promise;
    writeCache(key, result);
    return result;
}

/**
 * @param {string} key          identifies the payload; include anything that
 *                              changes the response, such as a filter
 * @param {() => Promise<any>} fetcher
 * @param {{ttlMs?: number, enabled?: boolean}} options
 */
export function useCachedResource(key, fetcher, { ttlMs = 60_000, enabled = true } = {}) {
    const cached = enabled ? readCache(key) : null;

    const [data, setData] = useState(cached ? cached.data : null);
    const [updatedAt, setUpdatedAt] = useState(cached ? cached.at : null);
    // Only a cold read blocks: with something cached there is nothing to wait for.
    const [loading, setLoading] = useState(enabled && !cached);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    // Kept in a ref so a fetcher defined inline does not restart the effect on
    // every render.
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const run = useCallback(
        async ({ background }) => {
            if (!enabled) return;

            if (background) setRefreshing(true);
            else setLoading(true);

            try {
                // One request per key, however many callers want it.
                let promise = inFlight.get(key);
                if (!promise) {
                    promise = Promise.resolve().then(() => fetcherRef.current());
                    inFlight.set(key, promise);
                    promise.finally(() => inFlight.delete(key));
                }

                const result = await promise;
                writeCache(key, result);

                if (!mounted.current) return;
                setData(result);
                setUpdatedAt(Date.now());
                setError("");
            } catch (err) {
                if (!mounted.current) return;
                // A failed background refresh leaves what is on screen alone —
                // stale numbers beat an error page over numbers that were fine.
                setError(err?.message || "Failed to load");
            } finally {
                if (mounted.current) {
                    setLoading(false);
                    setRefreshing(false);
                }
            }
        },
        [key, enabled]
    );

    useEffect(() => {
        if (!enabled) return;

        const entry = readCache(key);
        const isFresh = entry && Date.now() - entry.at < ttlMs;

        if (entry) {
            setData(entry.data);
            setUpdatedAt(entry.at);
            setLoading(false);
        }

        // The whole point: inside the TTL nothing is requested.
        if (isFresh) return;

        run({ background: Boolean(entry) });
    }, [key, ttlMs, enabled, run]);

    /** An explicit refresh always goes to the server. */
    const refresh = useCallback(() => run({ background: true }), [run]);

    return { data, loading, refreshing, error, updatedAt, refresh };
}

export default useCachedResource;
